import Papa from 'papaparse';
import { useCallback, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';

import {
	addToDuplicateIndex,
	autoDetectMapping,
	buildDuplicateIndex,
	cellsForRecord,
	createEmptyRecord,
	dupKeysOf,
	type DuplicateIndex,
	evaluateRecord,
	type FilterStatus,
	getStatusCounts,
	type IColumnMapping,
	type ImportPhase,
	type IProcessingResult,
	type IProcessingState,
	type ISortConfig,
	type IVoterRecord,
	mappingToIndexes,
	normalizeNigerianPhone,
	removeFromDuplicateIndex,
	toApiPayload,
	validatePhoneFormat,
	type VoterRecordOutcome,
} from '../app/import-voters/components/ImportVoterHelpers';
import { useVoterWorker } from '../app/import-voters/hooks/use-voter-worker';
import {
	BASELINE_CAPABILITIES,
	completeImportBatch,
	createImportBatch,
	createVoter,
	fetchAllImportFailures,
	fetchImportBatch,
	fetchImportFailuresCsv,
	IMPORT_MAPPED_FIELDS,
	type IVoterCapabilities,
	putImportChunk,
} from '../app/voters/api/voters-api';
import { type IBatchTransport, type IUploadControl, runBatchUpload } from '../app/voters/engine/batch-uploader';
import { runLegacyUpload } from '../app/voters/engine/legacy-row-uploader';
import {
	clearResumeEntry,
	findResumeEntry,
	fingerprintFile,
	type IResumeEntry,
	saveResumeEntry,
} from '../app/voters/engine/resume-store';
import { buildRowsToFixCsv, type IRowToFix, rowsToFixFileName } from '../app/voters/engine/rows-to-fix';
import { describeReasonCode, errorCodeOf, isAlreadyRegisteredCode, readVoterError } from '../app/voters/engine/voter-errors';
import type { VoterRulesMode } from '../app/voters/engine/voter-rules';
import { VOTERS_COPY } from '../app/voters/Voters.Copy';

const INITIAL_PROCESSING_STATE: IProcessingState = {
	isProcessing: false,
	isPaused: false,
	total: 0,
	processed: 0,
	passed: 0,
	failed: 0,
	results: [],
	added: 0,
	alreadyRegistered: 0,
	rejected: 0,
	heldBack: 0,
	mode: null,
	batchNumber: null,
	batchId: null,
	waiting: null,
	fatalError: null,
	wasAborted: false,
	sameFileNumbers: [],
	reconciling: false,
	notice: null,
};

const SEARCH_DEBOUNCE_MS = 300;

/** The extensions the parser can read. Anything else is refused with a visible reason. */
export const IMPORT_EXTENSIONS = ['xlsx', 'xls', 'csv'] as const;

const fileExtension = (name: string) => name.split('.').pop()?.toLowerCase() ?? '';

export const wrongExtensionMessage = (name: string): string | null => {
	const ext = fileExtension(name);
	if ((IMPORT_EXTENSIONS as readonly string[]).includes(ext)) return null;
	return `${name} is not a spreadsheet. Upload an .xlsx, .xls or .csv file — or download a template above.`;
};

const transport: IBatchTransport = {
	createBatch: createImportBatch,
	putChunk: putImportChunk,
	getBatch: fetchImportBatch,
	complete: completeImportBatch,
};

/** field → column index, server fields only — reference columns (state name, …) are never sent. */
const serverColumnMapping = (headers: readonly string[], mapping: IColumnMapping): Record<string, number> => {
	const all = mappingToIndexes(headers, mapping);
	return Object.fromEntries(Object.entries(all).filter(([field]) => (IMPORT_MAPPED_FIELDS as readonly string[]).includes(field)));
};

const outcomeForCode = (code: string): VoterRecordOutcome => (isAlreadyRegisteredCode(code) ? 'already' : 'fix');

/** Why a batch stopped, in words. A 409 on a chunk means the file no longer matches the batch. */
const fatalMessage = (error: unknown, batchNumber: number): string => {
	const status = (error as { response?: { status?: number } })?.response?.status;
	const code = errorCodeOf((error as { response?: { data?: unknown } })?.response?.data);
	if (code === 'BATCH_CLOSED')
		return `Import #${batchNumber} is already finished or abandoned. Start a new import for the remaining rows.`;
	if (code === 'BATCH_NOT_FOUND') return `Import #${batchNumber} no longer exists. Start a new import.`;
	if (status === 409) {
		return `This file no longer matches Import #${batchNumber} — rows were changed since it started. Start a new import instead.`;
	}
	return readVoterError(error).message;
};

export const useImportVoterProps = () => {
	// Phase
	const [phase, setPhase] = useState<ImportPhase>('upload');

	// File data — records stored in ref to avoid cloning 1M-element arrays on setState
	const recordsRef = useRef<IVoterRecord[]>([]);
	const [recordsVersion, setRecordsVersion] = useState(0);
	const dupIndexRef = useRef<DuplicateIndex>(new Map());
	const fileBufferRef = useRef<ArrayBuffer | null>(null);

	const [rawHeaders, setRawHeaders] = useState<string[]>([]);
	const [columnMapping, setColumnMapping] = useState<IColumnMapping | null>(null);
	const [previewRows, setPreviewRows] = useState<string[][]>([]);
	const [fileName, setFileName] = useState('');
	/** A file the page refused (wrong extension, unreadable, no header row) — shown inline. */
	const [fileError, setFileError] = useState<string | null>(null);

	// Fingerprint + any unfinished batch this file belongs to.
	const fileHashRef = useRef<{ sha256: string; fingerprint: string }>({ sha256: '', fingerprint: '' });
	const [resumeCandidate, setResumeCandidate] = useState<IResumeEntry | null>(null);
	const resumeChosenRef = useRef<IResumeEntry | null>(null);
	const [resumingNumber, setResumingNumber] = useState<number | null>(null);

	// Which backend the rules and the uploader target. Set by the Import tab once the probe answers;
	// the provider is global, so it must not probe on every page itself.
	const [rulesMode, setRulesModeState] = useState<VoterRulesMode>('baseline');
	const rulesModeRef = useRef<VoterRulesMode>('baseline');
	const capabilitiesRef = useRef<IVoterCapabilities>(BASELINE_CAPABILITIES);
	// Batch imports send every row (the server records the rejects); legacy sends valid rows only.
	const [sendsEveryRow, setSendsEveryRow] = useState(false);

	// Table controls
	const [rawSearchTerm, setRawSearchTerm] = useState('');
	const [searchTerm, setSearchTerm] = useState('');
	const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
	const [sortConfig, setSortConfig] = useState<ISortConfig>({ field: null, direction: 'asc' });

	// Processing
	const [processingState, setProcessingState] = useState<IProcessingState>(INITIAL_PROCESSING_STATE);
	const controlRef = useRef<IUploadControl>({ aborted: false, paused: false });

	const { parseFile: workerParseFile, progress: parseProgress, isParsing, resetProgress } = useVoterWorker();

	// Bump version to trigger useMemo recalculations
	const bumpRecords = useCallback(() => {
		setRecordsVersion((v) => v + 1);
	}, []);

	const revalidateAll = useCallback(
		(mode: VoterRulesMode) => {
			const records = recordsRef.current;
			dupIndexRef.current = buildDuplicateIndex(records);
			for (let i = 0; i < records.length; i++) records[i] = evaluateRecord(records[i], dupIndexRef.current, mode);
			bumpRecords();
		},
		[bumpRecords]
	);

	/** Called by the Import tab when the capability probe resolves. Re-checks every row on a change. */
	const setCapabilities = useCallback(
		(capabilities: IVoterCapabilities) => {
			capabilitiesRef.current = capabilities;
			setSendsEveryRow(capabilities.importBatches);
			const mode: VoterRulesMode = capabilities.createV2 || capabilities.importBatches ? 'v2' : 'baseline';
			if (mode === rulesModeRef.current) return;
			rulesModeRef.current = mode;
			setRulesModeState(mode);
			if (recordsRef.current.length) revalidateAll(mode);
		},
		[revalidateAll]
	);

	// --- Debounced search ---
	const handleSearchChange = useCallback((value: string) => {
		setRawSearchTerm(value);
		if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
		searchTimerRef.current = setTimeout(() => {
			setSearchTerm(value);
		}, SEARCH_DEBOUNCE_MS);
	}, []);

	// --- File Parsing (header-only on main thread) ---

	const parseFileHeaders = useCallback((file: File) => {
		const refusal = wrongExtensionMessage(file.name);
		if (refusal) {
			setFileError(refusal);
			return;
		}
		setFileError(null);
		setFileName(file.name);
		setResumeCandidate(null);
		setResumingNumber(null);
		resumeChosenRef.current = null;
		const reader = new FileReader();

		reader.onerror = () => setFileError(`We couldn't read ${file.name}. Check the file isn't open elsewhere, then try again.`);

		reader.onload = (event) => {
			const buffer = event.target?.result as ArrayBuffer;
			if (!buffer) return;

			// Store full buffer for later worker parse
			fileBufferRef.current = buffer;

			let headers: string[] = [];
			let dataRows: string[][] = [];

			try {
				if (fileExtension(file.name) === 'csv') {
					const decoder = new TextDecoder('utf-8');
					const text = decoder.decode(buffer);
					const result = Papa.parse<string[]>(text, {
						header: false,
						preview: 5,
						skipEmptyLines: true,
					});
					if (result.data.length > 0) {
						headers = result.data[0].map((h: string) =>
							String(h)
								.replace(/^\uFEFF/, '')
								.trim()
						);
						dataRows = result.data.slice(1);
					}
				} else {
					// XLSX — read only first 5 rows for header detection
					const workbook = XLSX.read(buffer, { type: 'array', sheetRows: 5 });
					const sheetName = workbook.SheetNames[0];
					const worksheet = workbook.Sheets[sheetName];
					const jsonData: string[][] = XLSX.utils.sheet_to_json(worksheet, {
						header: 1,
						blankrows: false,
						defval: '',
					});
					if (jsonData.length > 0) {
						headers = jsonData[0].map((h) => String(h).trim());
						dataRows = jsonData.slice(1).filter((row) => row.some((cell) => String(cell).trim()));
					}
				}
			} catch {
				setFileError(`We couldn't read ${file.name} as a spreadsheet. Save it again as .xlsx or .csv and retry.`);
				return;
			}

			if (!headers.length) {
				setFileError(`${file.name} has no header row. The first row must name the columns — see the template.`);
				return;
			}

			setRawHeaders(headers);
			setPreviewRows(dataRows);

			const mapping = autoDetectMapping(headers);
			setColumnMapping(mapping);

			// Fingerprint in the background — only needed if the server can resume.
			void fingerprintFile(buffer, { name: file.name, size: file.size, lastModified: file.lastModified }).then((hash) => {
				fileHashRef.current = hash;
				if (capabilitiesRef.current.importBatches) setResumeCandidate(findResumeEntry(hash.fingerprint));
			});
		};

		reader.readAsArrayBuffer(file);
	}, []);

	const applyMappingAndContinue = useCallback(async () => {
		if (!columnMapping || !fileBufferRef.current) return;

		try {
			const result = await workerParseFile(fileBufferRef.current, fileName, columnMapping, rawHeaders);

			// Records arrived in chunks and were assembled in the hook
			recordsRef.current = result.records;
			fileBufferRef.current = null; // Release memory

			// Validate on the main thread (the worker sent raw records with _status='pending').
			revalidateAll(rulesModeRef.current);
			setPhase('review');
		} catch (err) {
			// Error is shown via parseProgress state (isParsing goes false, progress.phase='error')
			console.error('[ImportVoters] applyMappingAndContinue failed:', err);
		}
	}, [columnMapping, fileName, rawHeaders, workerParseFile, revalidateAll]);

	const updateMapping = useCallback((field: keyof IColumnMapping, headerName: string) => {
		setColumnMapping((prev) => (prev ? { ...prev, [field]: headerName } : prev));
	}, []);

	// --- Filtering & Sorting ---

	const filteredRecords = useMemo(() => {
		// recordsVersion is a dependency to trigger recomputation when records mutate
		void recordsVersion;
		let result = recordsRef.current;

		if (filterStatus !== 'all') {
			result = result.filter((r) => r._status === filterStatus);
		}

		if (searchTerm.trim()) {
			const term = searchTerm.toLowerCase().trim();
			result = result.filter(
				(r) =>
					r.voterRegistrationNumber.toLowerCase().includes(term) ||
					r.firstName.toLowerCase().includes(term) ||
					r.lastName.toLowerCase().includes(term) ||
					r.phoneNumber.toLowerCase().includes(term) ||
					r.pollingUnitCode.toLowerCase().includes(term) ||
					r.email.toLowerCase().includes(term)
			);
		}

		if (sortConfig.field) {
			const { field, direction } = sortConfig;
			result = [...result].sort((a, b) => {
				const aVal = String(a[field] || '').toLowerCase();
				const bVal = String(b[field] || '').toLowerCase();
				const cmp = aVal.localeCompare(bVal);
				return direction === 'asc' ? cmp : -cmp;
			});
		}

		return result;
	}, [recordsVersion, filterStatus, searchTerm, sortConfig]);

	const handleSort = useCallback((field: keyof IVoterRecord) => {
		setSortConfig((prev) => ({
			field,
			direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
		}));
	}, []);

	// --- Selection ---

	const toggleSelect = useCallback(
		(rowId: string) => {
			const records = recordsRef.current;
			const idx = records.findIndex((r) => r._rowId === rowId);
			if (idx !== -1) {
				records[idx] = { ...records[idx], _selected: !records[idx]._selected };
				bumpRecords();
			}
		},
		[bumpRecords]
	);

	const selectAll = useCallback(() => {
		const filteredIds = new Set(filteredRecords.map((r) => r._rowId));
		const records = recordsRef.current;
		for (let i = 0; i < records.length; i++) {
			if (filteredIds.has(records[i]._rowId)) {
				records[i] = { ...records[i], _selected: true };
			}
		}
		bumpRecords();
	}, [filteredRecords, bumpRecords]);

	const deselectAll = useCallback(() => {
		const records = recordsRef.current;
		for (let i = 0; i < records.length; i++) {
			if (records[i]._selected) {
				records[i] = { ...records[i], _selected: false };
			}
		}
		bumpRecords();
	}, [bumpRecords]);

	const selectValidOnly = useCallback(() => {
		const records = recordsRef.current;
		for (let i = 0; i < records.length; i++) {
			records[i] = { ...records[i], _selected: records[i]._status === 'valid' };
		}
		bumpRecords();
	}, [bumpRecords]);

	// --- Duplicate re-checks ---

	/** Re-evaluate every row sharing a phone or VIN with `record` — the "first copy" may have moved. */
	const revalidateSiblings = useCallback((record: Pick<IVoterRecord, 'phoneNumber' | 'voterRegistrationNumber'>) => {
		const index = dupIndexRef.current;
		const ids = new Set<string>();
		dupKeysOf(record).forEach((k) => index.get(k)?.forEach((e) => ids.add(e.rowId)));
		if (!ids.size) return;
		const records = recordsRef.current;
		for (let i = 0; i < records.length; i++) {
			if (ids.has(records[i]._rowId)) records[i] = evaluateRecord(records[i], index, rulesModeRef.current);
		}
	}, []);

	// --- Editing ---

	const updateRecord = useCallback(
		(rowId: string, field: keyof IVoterRecord, value: string) => {
			const records = recordsRef.current;
			const idx = records.findIndex((r) => r._rowId === rowId);
			if (idx === -1) return;

			const record = records[idx];

			// Phone normalization on phone field edit
			let finalValue = value;
			let phoneNormalized = record._phoneNormalized;
			let phoneWarning = record._phoneWarning;

			if (field === 'phoneNumber') {
				const phoneResult = normalizeNigerianPhone(value);
				finalValue = phoneResult.value;
				phoneNormalized = phoneResult.wasNormalized;
				phoneWarning = validatePhoneFormat(phoneResult.value);
			}

			// A row the server rejected goes back to review once someone fixes it.
			const wasRejected = record._status === 'failed';
			const updated: IVoterRecord = {
				...record,
				[field]: finalValue,
				_phoneNormalized: phoneNormalized,
				_phoneWarning: phoneWarning,
				_status: wasRejected ? 'pending' : record._status,
				_outcome: wasRejected ? undefined : record._outcome,
				_apiError: wasRejected ? undefined : record._apiError,
			};

			const keyChanged = field === 'phoneNumber' || field === 'voterRegistrationNumber';
			if (keyChanged) {
				removeFromDuplicateIndex(dupIndexRef.current, record);
				addToDuplicateIndex(dupIndexRef.current, updated);
			}

			records[idx] = evaluateRecord(updated, dupIndexRef.current, rulesModeRef.current);

			if (keyChanged) {
				revalidateSiblings(record);
				revalidateSiblings(updated);
			}

			bumpRecords();
		},
		[bumpRecords, revalidateSiblings]
	);

	const addRecord = useCallback(() => {
		const nextRow = recordsRef.current.reduce((max, r) => Math.max(max, r._rowNumber), 1) + 1;
		const newRecord = createEmptyRecord(nextRow, rulesModeRef.current);
		recordsRef.current = [newRecord, ...recordsRef.current];
		bumpRecords();
	}, [bumpRecords]);

	const removeRecord = useCallback(
		(rowId: string) => {
			const records = recordsRef.current;
			const idx = records.findIndex((r) => r._rowId === rowId);
			if (idx === -1) return;

			const record = records[idx];
			removeFromDuplicateIndex(dupIndexRef.current, record);
			recordsRef.current = records.filter((r) => r._rowId !== rowId);
			revalidateSiblings(record);
			bumpRecords();
		},
		[bumpRecords, revalidateSiblings]
	);

	const removeSelected = useCallback(() => {
		const removed = recordsRef.current.filter((r) => r._selected);
		removed.forEach((r) => removeFromDuplicateIndex(dupIndexRef.current, r));
		recordsRef.current = recordsRef.current.filter((r) => !r._selected);
		removed.forEach((r) => revalidateSiblings(r));
		bumpRecords();
	}, [bumpRecords, revalidateSiblings]);

	// --- Processing ---

	const markRecord = useCallback((rowId: string, patch: Partial<IVoterRecord>) => {
		const records = recordsRef.current;
		const idx = records.findIndex((r) => r._rowId === rowId);
		if (idx !== -1) records[idx] = { ...records[idx], ...patch };
	}, []);

	/**
	 * Valid rows import; bad rows are reported. In batch mode EVERY row goes to the server (it records
	 * the rejects against the batch, so they appear in Import history); in legacy mode only valid rows
	 * are sent and the rest are held back locally. Either way the result screen counts the same three
	 * things, and "Download rows to fix" lists every row that did not import.
	 */
	const startProcessing = useCallback(
		async (selectedOnly: boolean) => {
			const records = recordsRef.current;
			const candidates = (selectedOnly ? records.filter((r) => r._selected) : records).filter(
				(r) => r._status !== 'success' && r._status !== 'processing'
			);
			if (!candidates.length || !columnMapping) return;

			const capabilities = capabilitiesRef.current;
			const mode = rulesModeRef.current;
			const useBatch = capabilities.importBatches;
			const sendable = useBatch ? candidates : candidates.filter((r) => r._status === 'valid');
			const heldBack = useBatch ? 0 : candidates.length - sendable.length;

			controlRef.current = { aborted: false, paused: false };
			setPhase('processing');
			setProcessingState({
				...INITIAL_PROCESSING_STATE,
				isProcessing: true,
				total: sendable.length,
				heldBack,
				mode: useBatch ? 'batch' : 'legacy',
			});

			const results: IProcessingResult[] = [];
			const tally = { added: 0, already: 0, rejected: 0, processed: 0 };
			const push = (record: IVoterRecord, outcome: VoterRecordOutcome, reasonCode?: string, error?: string) => {
				results.push({
					rowId: record._rowId,
					rowNumber: record._rowNumber,
					vin: record.voterRegistrationNumber,
					name: `${record.firstName} ${record.lastName}`.trim(),
					success: outcome === 'added',
					outcome,
					reasonCode,
					error,
				});
				tally.processed += 1;
				if (outcome === 'added') tally.added += 1;
				else if (outcome === 'already') tally.already += 1;
				else tally.rejected += 1;
				markRecord(record._rowId, {
					_status: outcome === 'added' ? 'success' : 'failed',
					_outcome: outcome,
					_apiError: error,
					_reasonCodes: reasonCode ? [reasonCode] : [],
				});
			};
			const publish = (extra: Partial<IProcessingState> = {}) =>
				setProcessingState((prev) => ({
					...prev,
					processed: tally.processed,
					passed: tally.added,
					failed: tally.already + tally.rejected,
					added: tally.added,
					alreadyRegistered: tally.already,
					rejected: tally.rejected,
					results: [...results],
					...extra,
				}));

			if (useBatch) {
				const resume = resumeChosenRef.current;
				const chunkSize = resume?.chunkSize ?? capabilities.maxChunkRows;
				// The server numbers a chunk's rows firstRowNumber + i, so the rows sent must be numbered
				// consecutively. Sheet row 2 onward, in send order — identical to the file's own numbering
				// whenever the whole file goes up unedited.
				const sent = sendable.map((record, i) => ({ record, rowNumber: i + 2 }));
				let batchNumber = resume?.number ?? 0;
				const outcome = await runBatchUpload({
					transport,
					rows: sent.map((s) => ({ rowNumber: s.rowNumber, cells: cellsForRecord(s.record, rawHeaders, columnMapping) })),
					chunkSize,
					resume: resume ? { id: resume.batchId, number: resume.number } : undefined,
					create: {
						fileName,
						...(fileHashRef.current.sha256 ? { fileSha256: fileHashRef.current.sha256 } : {}),
						headers: rawHeaders,
						columnMapping: serverColumnMapping(rawHeaders, columnMapping),
					},
					control: controlRef.current,
					onBatchCreated: (batch) => {
						batchNumber = batch.number;
						setProcessingState((prev) => ({
							...prev,
							batchNumber: batch.number,
							batchId: batch.id,
							sameFileNumbers: batch.sameFileNumbers,
						}));
						saveResumeEntry({
							fingerprint: fileHashRef.current.fingerprint,
							batchId: batch.id,
							number: batch.number,
							fileName,
							rowCount: sendable.length,
							chunkSize,
							mapping: { ...columnMapping },
							savedAt: Date.now(),
						});
					},
					// A chunk answer is counts only; which rows failed is read once, at the end.
					onChunk: (_index, rows, result) => {
						tally.added += result.added;
						tally.rejected += result.failed;
						tally.processed += rows.length;
						publish({ waiting: null });
					},
					onProgress: (p) =>
						setProcessingState((prev) => ({
							...prev,
							waiting: p.waiting ?? null,
							isPaused: p.waiting === 'signed-out' || prev.isPaused,
						})),
				});

				if (outcome.outcome === 'completed') clearResumeEntry(outcome.batchId);
				resumeChosenRef.current = null;
				setResumingNumber(null);

				// Reconcile row by row from the server's failure list: every confirmed row not in it was added.
				let reconcileError: string | null = null;
				if (outcome.batchId) {
					setProcessingState((prev) => ({ ...prev, reconciling: true }));
					const confirmedRows = Math.min(sent.length, outcome.nextChunkIndex * chunkSize);
					try {
						const failures = await fetchAllImportFailures(outcome.batchId);
						const failedByRow = new Map(failures.map((f) => [f.rowNumber, f]));
						tally.added = 0;
						tally.already = 0;
						tally.rejected = 0;
						tally.processed = 0;
						results.length = 0;
						sent.slice(0, confirmedRows).forEach(({ record, rowNumber }) => {
							const failure = failedByRow.get(rowNumber);
							if (!failure) push(record, 'added');
							else
								push(
									record,
									outcomeForCode(failure.reasonCode),
									failure.reasonCode,
									failure.reason || describeReasonCode(failure.reasonCode)
								);
						});
					} catch {
						// Counts stand (from the chunk answers); only the per-row split is missing.
						reconcileError = VOTERS_COPY.import.reconcileFailed(batchNumber);
					}
				}
				publish({
					isProcessing: false,
					isPaused: false,
					reconciling: false,
					waiting: null,
					wasAborted: outcome.outcome === 'aborted',
					fatalError: outcome.outcome === 'failed' ? fatalMessage(outcome.error, batchNumber) : null,
					notice: reconcileError,
				});
			} else {
				const summary = await runLegacyUpload({
					jobs: sendable.map((r) => ({ key: r._rowId, payload: toApiPayload(r, mode) })),
					send: createVoter,
					control: controlRef.current,
					onStart: (key) => {
						markRecord(key, { _status: 'processing' });
						bumpRecords();
					},
					onSignedOut: () => setProcessingState((prev) => ({ ...prev, isPaused: true, waiting: 'signed-out' })),
					onRow: (key, rowOutcome) => {
						const record = recordsRef.current.find((r) => r._rowId === key);
						if (!record) return;
						if (rowOutcome.kind === 'added') push(record, 'added');
						else push(record, rowOutcome.kind, rowOutcome.code, rowOutcome.message);
						publish({ waiting: null });
						bumpRecords();
					},
				});

				// Rows the stop left un-sent go back to review rather than reading as "processing".
				if (summary.outcome === 'aborted') {
					const all = recordsRef.current;
					for (let i = 0; i < all.length; i++) {
						if (all[i]._status === 'processing')
							all[i] = evaluateRecord({ ...all[i], _status: 'pending' }, dupIndexRef.current, mode);
					}
				}
				publish({ isProcessing: false, isPaused: false, waiting: null, wasAborted: summary.outcome === 'aborted' });
			}

			bumpRecords();
		},
		[columnMapping, rawHeaders, fileName, bumpRecords, markRecord]
	);

	const pauseProcessing = useCallback(() => {
		controlRef.current.paused = true;
		setProcessingState((prev) => ({ ...prev, isPaused: true }));
	}, []);

	const resumeProcessing = useCallback(() => {
		controlRef.current.paused = false;
		setProcessingState((prev) => ({ ...prev, isPaused: false, waiting: prev.waiting === 'signed-out' ? null : prev.waiting }));
	}, []);

	const abortProcessing = useCallback(() => {
		controlRef.current.aborted = true;
		controlRef.current.paused = false;
		setProcessingState((prev) => ({ ...prev, isPaused: false }));
	}, []);

	const backToReview = useCallback(() => {
		setPhase('review');
		setProcessingState(INITIAL_PROCESSING_STATE);
	}, []);

	// --- Resume an unfinished batch ---

	/** Adopt the stored batch: its mapping now, and its id when the import starts. */
	const resumeImport = useCallback(() => {
		if (!resumeCandidate) return;
		resumeChosenRef.current = resumeCandidate;
		setResumingNumber(resumeCandidate.number);
		setColumnMapping({ ...autoDetectMapping(rawHeaders), ...(resumeCandidate.mapping as Partial<IColumnMapping>) });
		setResumeCandidate(null);
	}, [resumeCandidate, rawHeaders]);

	const dismissResume = useCallback(() => {
		setResumeCandidate(null);
		setResumingNumber(null);
		resumeChosenRef.current = null;
	}, []);

	// --- Reports ---

	/** Every row that did not import: held back by the review, or rejected by the server. */
	const rowsToFix = useMemo((): IRowToFix[] => {
		void recordsVersion;
		return recordsRef.current
			.filter((r) => r._status === 'invalid' || r._status === 'duplicate' || (r._status === 'failed' && r._outcome !== 'already'))
			.sort((a, b) => a._rowNumber - b._rowNumber)
			.map((r) => ({
				cells: columnMapping ? cellsForRecord(r, rawHeaders, columnMapping) : r._cells,
				whatToFix: r._apiError || r._errors.join(' '),
			}));
	}, [recordsVersion, columnMapping, rawHeaders]);

	const { batchId } = processingState;

	/**
	 * After a batch import the server's file is THE rows-to-fix file — the same one Import history
	 * serves, already-registered rows included — so the two downloads can never disagree. Before an
	 * import, in legacy mode, or if that request fails, the file is built from the rows here.
	 */
	const downloadRowsToFix = useCallback(async () => {
		const save = (blob: Blob) => {
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = rowsToFixFileName(fileName);
			document.body.appendChild(a);
			a.click();
			a.remove();
			setTimeout(() => URL.revokeObjectURL(url), 1000);
		};
		if (batchId) {
			try {
				save(await fetchImportFailuresCsv(batchId));
				return;
			} catch {
				// Fall through to the local file: it still lists every row to fix.
			}
		}
		if (!rowsToFix.length) return;
		save(new Blob([buildRowsToFixCsv(rawHeaders, rowsToFix)], { type: 'text/csv;charset=utf-8;' }));
	}, [batchId, rowsToFix, rawHeaders, fileName]);

	const downloadReport = useCallback(() => {
		const { results } = processingState;
		const label: Record<VoterRecordOutcome, string> = { added: 'Added', already: 'Already registered', fix: 'To fix' };
		const wsData = [
			['Row', 'VIN', 'Name', 'Outcome', 'Detail'],
			...results.map((r) => [r.rowNumber, r.vin, r.name, label[r.outcome], r.error || '']),
		];

		const ws = XLSX.utils.aoa_to_sheet(wsData);
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, 'Import Report');
		XLSX.writeFile(wb, `voter-import-report-${Date.now()}.xlsx`);
	}, [processingState]);

	// --- Reset ---

	const clearAll = useCallback(() => {
		setPhase('upload');
		recordsRef.current = [];
		dupIndexRef.current = new Map();
		fileBufferRef.current = null;
		fileHashRef.current = { sha256: '', fingerprint: '' };
		resumeChosenRef.current = null;
		setResumeCandidate(null);
		setResumingNumber(null);
		setRecordsVersion(0);
		setRawHeaders([]);
		setPreviewRows([]);
		setColumnMapping(null);
		setFileName('');
		setFileError(null);
		setRawSearchTerm('');
		setSearchTerm('');
		setFilterStatus('all');
		setSortConfig({ field: null, direction: 'asc' });
		setProcessingState(INITIAL_PROCESSING_STATE);
		controlRef.current = { aborted: false, paused: false };
		resetProgress();
	}, [resetProgress]);

	const statusCounts = useMemo(() => {
		void recordsVersion;
		return getStatusCounts(recordsRef.current);
	}, [recordsVersion]);

	return {
		// Phase
		phase,
		setPhase,

		// Backend
		rulesMode,
		setCapabilities,

		// File data
		fileName,
		fileError,
		setFileError,
		rawHeaders,
		rawData: previewRows,
		columnMapping,
		parseFile: parseFileHeaders,
		updateMapping,
		applyMappingAndContinue,
		resumeCandidate,
		resumingNumber,
		resumeImport,
		dismissResume,

		// Records
		records: recordsRef.current,
		filteredRecords,
		statusCounts,

		// Table controls
		searchTerm,
		rawSearchTerm,
		handleSearchChange,
		filterStatus,
		setFilterStatus,
		sortConfig,
		handleSort,

		// Selection
		toggleSelect,
		selectAll,
		deselectAll,
		selectValidOnly,

		// Editing
		updateRecord,
		addRecord,
		removeRecord,
		removeSelected,

		// Processing
		processingState,
		sendsEveryRow,
		startProcessing,
		pauseProcessing,
		resumeProcessing,
		abortProcessing,
		backToReview,
		downloadReport,
		rowsToFix,
		downloadRowsToFix,

		// Worker
		parseProgress,
		isParsing,

		// Reset
		clearAll,
	};
};

export type ImportVoterProps = ReturnType<typeof useImportVoterProps>;
